"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { isVideoMedia, pickBestMediaFrames } from "@/lib/sign-media";
import { youTubeEmbedUrl, youTubeThumbnail, type SignVideo } from "@/lib/sign-video";
import type { DictionaryEntry } from "@/lib/dictionary";

const FRAME_INTERVAL_MS = 600;
const HANDOFF_KEY = "signpost.pendingChallenge";

type MediaState =
    | { status: "loading" }
    | { status: "frames"; frames: string[] }
    | { status: "video"; url: string }
    | { status: "unavailable" };

function YouTubeFacade({ video, term }: { video: SignVideo; term: string }) {
    const [playing, setPlaying] = useState(false);
    const label = `How to sign ${term} in ASL`;

    if (playing) {
        return (
            <div className="sign-media sign-media--embed">
                <iframe
                    className="sign-media-frame"
                    src={youTubeEmbedUrl(video, true)}
                    title={label}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            </div>
        );
    }

    return (
        <button
            type="button"
            className="sign-media sign-media--facade"
            onClick={() => setPlaying(true)}
            aria-label={`Play video: ${label}`}
        >
            <img
                className="sign-media-asset"
                src={youTubeThumbnail(video)}
                alt={`Video thumbnail: ${label}`}
                loading="lazy"
            />
            <span className="sign-media-play" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86A1 1 0 0 0 8 5.14z" />
                </svg>
            </span>
        </button>
    );
}

export function SignMedia({ entry }: { entry: DictionaryEntry }) {
    const primary = entry.localMedia ?? (entry.video ? null : entry.staticMedia);
    const [media, setMedia] = useState<MediaState>(() => {
        if (!primary) return { status: "loading" };
        return isVideoMedia(primary)
            ? { status: "video", url: primary }
            : { status: "frames", frames: [primary] };
    });
    const [frameIndex, setFrameIndex] = useState(0);

    useEffect(() => {
        if (primary || entry.video) return;
        if (!entry.hintUrl) {
            setMedia({ status: "unavailable" });
            return;
        }

        let cancelled = false;
        fetch(`/api/hint-media?url=${encodeURIComponent(entry.hintUrl)}`)
            .then((response) => (response.ok ? response.json() : null))
            .then((payload: { frames?: unknown } | null) => {
                if (cancelled) return;
                const list = payload && Array.isArray(payload.frames) ? payload.frames : [];
                const raw = list.filter(
                    (frame): frame is string =>
                        typeof frame === "string" && frame.trim().length > 0,
                );
                const selected = pickBestMediaFrames(entry.hintUrl, raw);
                const images = selected.filter((frame) => !isVideoMedia(frame));
                if (images.length > 0) {
                    setMedia({ status: "frames", frames: images });
                    return;
                }
                const video = selected.find((frame) => isVideoMedia(frame));
                setMedia(video ? { status: "video", url: video } : { status: "unavailable" });
            })
            .catch(() => {
                if (!cancelled) setMedia({ status: "unavailable" });
            });

        return () => {
            cancelled = true;
        };
    }, [entry.hintUrl, entry.video, primary]);

    useEffect(() => {
        if (media.status !== "frames" || media.frames.length < 2) return;
        const total = media.frames.length;
        const timer = window.setInterval(() => {
            setFrameIndex((index) => (index + 1) % total);
        }, FRAME_INTERVAL_MS);
        return () => window.clearInterval(timer);
    }, [media]);

    if (!entry.localMedia && entry.video) {
        return <YouTubeFacade video={entry.video} term={entry.term} />;
    }

    if (media.status === "loading") {
        return (
            <div className="sign-media sign-media--loading">
                <div className="sign-media-spinner" aria-hidden="true" />
                <span className="sign-media-note">Loading reference…</span>
            </div>
        );
    }

    if (media.status === "unavailable") {
        return (
            <div className="sign-media sign-media--empty">
                <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="m3 15 5-5 4 4 3-3 6 6" />
                </svg>
                <span className="sign-media-note">
                    No reference clip available for this sign yet. Follow the written steps and
                    check yourself with the camera.
                </span>
            </div>
        );
    }

    if (media.status === "video") {
        return (
            <div className="sign-media">
                <video
                    className="sign-media-asset"
                    src={media.url}
                    muted
                    loop
                    playsInline
                    autoPlay
                    onError={() => setMedia({ status: "unavailable" })}
                />
            </div>
        );
    }

    const safeIndex = frameIndex % media.frames.length;

    return (
        <div className="sign-media">
            <img
                className="sign-media-asset"
                src={media.frames[safeIndex]}
                alt={`Demonstration of the ASL sign for ${entry.term}`}
                onError={() => setMedia({ status: "unavailable" })}
            />
            {media.frames.length > 1 && (
                <div className="sign-media-dots" aria-hidden="true">
                    {media.frames.map((frame, index) => (
                        <span
                            key={frame}
                            className={`sign-media-dot${index === safeIndex ? " sign-media-dot--on" : ""}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export function PracticeLauncher({ entry }: { entry: DictionaryEntry }) {
    const router = useRouter();
    const [blocked, setBlocked] = useState(false);
    const model = entry.model;

    function launch() {
        if (!model) return;
        const id = `dictionary-${entry.slug}`;
        const payload = {
            id,
            title: entry.term,
            unitTitle: "Dictionary",
            description: `Practice the sign for ${entry.term}. Hold the sign until Signpost recognises it.`,
            type: "lesson" as const,
            words: [{ word: entry.label, model }],
            returnTo: `/dictionary/${entry.slug}`,
        };

        try {
            window.sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(payload));
        } catch {
            setBlocked(true);
            return;
        }
        router.push(`/lesson?challenge=${encodeURIComponent(id)}`);
    }

    if (!model) {
        return (
            <div className="sign-practice">
                <span className="sign-practice-note">
                    Camera practice isn&apos;t available for this sign yet. Use the clip above as
                    your reference while we train the model on it.
                </span>
            </div>
        );
    }

    return (
        <div className="sign-practice">
            <button type="button" className="sign-practice-btn" onClick={launch}>
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <path d="M16.5 7.5V6a2 2 0 0 0-2-2h-9a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-1.5" />
                    <path d="m17 9 3-2v10l-3-2" />
                </svg>
                <span>Try it with your camera</span>
            </button>
            <span className="sign-practice-note">
                {blocked
                    ? "Your browser blocked session storage, so practice mode can't start."
                    : "Runs in your browser. Video never leaves your device."}
            </span>
        </div>
    );
}
